import { PropsWithChildren, useMemo } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import styled from "styled-components";
import { GRAY, LIGHT_GRAY, NEAR_WHITE } from "../../config";
import { formatNativeDateInputValue } from "../../utils";
import { BaseField, BaseFieldProps } from "./BaseField";

const Input = styled.input<{ background?: string }>`
  width: 100%;
  height: 36px;
  padding: 9px 15px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1px solid ${LIGHT_GRAY};
  background-color: ${({ background }) => background ?? GRAY};
  cursor: pointer;

  color: ${NEAR_WHITE};
  font-size: 14px;
  transition: 0.2s ease;
  outline: none;
  font-family: Roboto;

  ::-webkit-calendar-picker-indicator {
    filter: invert(0.9);
  }

  ::placeholder {
    color: ${NEAR_WHITE};
  }

  &:focus {
    border-color: ${LIGHT_GRAY};

    & + svg {
      color: ${LIGHT_GRAY};
    }
  }
`;

interface DateFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends BaseFieldProps<TFieldValues, TName> {
  placeholder?: string;
  min?: Date | string;
  max?: Date | string;
  defaultValue?: Date | string;
  background?: string;
}

export function DateField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  label,
  placeholder,
  defaultValue,
  rules,
  form,
  min,
  max,
  readOnly,
  background,
}: PropsWithChildren<DateFieldProps<TFieldValues, TName>>) {
  const minDate = useMemo(() => {
    if (!min) return undefined;
    return formatNativeDateInputValue(min);
  }, [min]);
  const maxDate = useMemo(() => {
    if (!max) return undefined;
    return formatNativeDateInputValue(max);
  }, [max]);
  return (
    <BaseField
      form={form}
      name={name}
      label={label}
      defaultValue={defaultValue && formatNativeDateInputValue(defaultValue)}
      rules={rules}
      fieldClassName="date-field"
      render={({ field }) => (
        <Input
          type="date"
          min={minDate}
          max={maxDate}
          name={name}
          onChange={field.onChange}
          value={
            typeof field.value === "string"
              ? field.value
              : formatNativeDateInputValue(field.value)
          }
          placeholder={placeholder}
          disabled={readOnly}
          background={background}
        />
      )}
    />
  );
}

DateField.defaultProps = {
  defaultValue: "",
};
